export interface UnityBuildConfig {
  loaderUrl: string;
  dataUrl: string;
  frameworkUrl: string;
  codeUrl: string;
  streamingAssetsUrl: string;
  companyName: string;
  productName: string;
  productVersion: string;
}

export interface RuntimeConfig {
  apiBaseUrl: string;
  healthUrl: string;
  environment: "development" | "staging" | "production";
  unity: UnityBuildConfig;
}

export const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  apiBaseUrl: "/api/v1",
  healthUrl: "/health/ready/",
  environment: "development",
  unity: {
    loaderUrl: "/unity/Build/super-werwolf.loader.js",
    dataUrl: "/unity/Build/super-werwolf.data.br",
    frameworkUrl: "/unity/Build/super-werwolf.framework.js.br",
    codeUrl: "/unity/Build/super-werwolf.wasm.br",
    streamingAssetsUrl: "/unity/StreamingAssets",
    companyName: "Kiese Dev",
    productName: "Super Werwolf",
    productVersion: "0.1.0",
  },
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requireString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`runtime-config: ${key} muss eine nicht-leere Zeichenkette sein`);
  }
  return value;
}

function requireLocalPath(record: Record<string, unknown>, key: string): string {
  const value = requireString(record, key);
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\") || /[\u0000-\u001f]/u.test(value)) {
    throw new Error(`runtime-config: ${key} muss ein lokaler absoluter Pfad sein`);
  }
  return value;
}

export function parseRuntimeConfig(value: unknown): RuntimeConfig {
  if (!isRecord(value)) {
    throw new Error("runtime-config: Wurzel muss ein Objekt sein");
  }
  if (!isRecord(value.unity)) {
    throw new Error("runtime-config: unity muss ein Objekt sein");
  }

  const environment = requireString(value, "environment");
  if (!(["development", "staging", "production"] as const).includes(environment as RuntimeConfig["environment"])) {
    throw new Error("runtime-config: ungültige Umgebung");
  }

  return {
    apiBaseUrl: requireLocalPath(value, "apiBaseUrl"),
    healthUrl: requireLocalPath(value, "healthUrl"),
    environment: environment as RuntimeConfig["environment"],
    unity: {
      loaderUrl: requireLocalPath(value.unity, "loaderUrl"),
      dataUrl: requireLocalPath(value.unity, "dataUrl"),
      frameworkUrl: requireLocalPath(value.unity, "frameworkUrl"),
      codeUrl: requireLocalPath(value.unity, "codeUrl"),
      streamingAssetsUrl: requireLocalPath(value.unity, "streamingAssetsUrl"),
      companyName: requireString(value.unity, "companyName"),
      productName: requireString(value.unity, "productName"),
      productVersion: requireString(value.unity, "productVersion"),
    },
  };
}

export async function loadRuntimeConfig(fetcher: typeof fetch = fetch): Promise<RuntimeConfig> {
  const response = await fetcher("/runtime-config.json", {
    cache: "no-store",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Konfiguration konnte nicht geladen werden (${response.status})`);
  }
  return parseRuntimeConfig(await response.json());
}
